// One definition of the bank account identifier keys and how they are named. The server
// ships `label` on every identifier it returns from here, so a client never has to fetch
// a country's format rules just to name a value it is already holding.

export const BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY = {
	iban: 'iban',
	swiftCode: 'bic',
	accountNumber: 'account_number',
	bankName: 'bank_name',
	accountHolder: 'account_holder',
} as const;

export type BankAccountFieldName = keyof typeof BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY;

// Labels for the keys the format-rule registry defines; an unknown (future) key falls
// back to a humanized form.
const IDENTIFIER_KEY_LABELS: Record<string, string> = {
	iban: 'IBAN',
	bic: 'BIC',
	account_number: 'Account number',
	sort_code: 'Sort code',
	routing_number: 'Routing number',
	bsb: 'BSB',
	ifsc_code: 'IFSC code',
	bank_code: 'Bank code',
	branch_code: 'Branch code',
	account_type: 'Account type',
	clabe: 'CLABE',
	pix_key: 'Pix key',
	pix_key_type: 'Pix key type',
	bank_name: 'Bank name',
	account_holder: 'Account holder',
};

export const bankAccountIdentifierLabel = (key: string): string =>
	IDENTIFIER_KEY_LABELS[key] ??
	`${key.charAt(0).toUpperCase()}${key.slice(1).replaceAll('_', ' ')}`;

// The holder and the bank render as their own field wherever an account is shown, never
// as one of its identifiers.
export const NAME_IDENTIFIER_KEYS = new Set<string>([
	BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY.bankName,
	BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY.accountHolder,
]);

// The identifying values the classic layout gives a field of its own.
export const CLASSIC_IDENTIFIER_KEYS = new Set<string>([
	BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY.iban,
	BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY.swiftCode,
	BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY.accountNumber,
]);

// Everything the classic layout covers, names included.
export const CANONICAL_IDENTIFIER_KEYS = new Set<string>(
	Object.values(BANK_ACCOUNT_FIELD_TO_IDENTIFIER_KEY)
);

// Values masked wherever an account is listed. Public bank/branch codes stay readable —
// they identify the institution, not the account — so a reviewer can still scan a row.
// The detail page renders everything in full.
export const SENSITIVE_IDENTIFIER_KEYS = new Set(['account_number', 'clabe', 'pix_key', 'iban']);
